<template>
    <div>
        <legend class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText">
            {{ $t('Trainerlizenzen') }}:
        </legend>
        <table class="kgr-table kgr-table--striped kgr-table--block table-groups kgr-row">
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Lizenzstufe') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Lizenzname/nummer') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Abteilung') }}
                    </th>
                    <th
                        class="kgr-table__cell kgr-table__cell--headline"
                    >
                        {{ $t('Sportart') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Ausgestellt/Vorgelegt am') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Gültig bis') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline"></th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="trainerLicense in trainerLicenses"
                    :key="trainerLicense.id + trainerLicense.invalid"
                    class="kgr-table__row kgr-table__row--top"
                    :class="{ 'kgr-backgroundColor--error': trainerLicense.invalid}"
                >
                    <td class="kgr-table__cell">
                        <template v-if="isBeingEdited(trainerLicense)">
                            <div class="form-widget without-label">
                                <kgr-trainer-license-level-select
                                    v-model="editTrainerLicenses[getEditIndex(trainerLicense)].level"
                                />
                            </div>
                        </template>
                        <template v-else>
                            {{ $t('trainerLicenses.level.' + trainerLicense.level) }}
                        </template>
                    </td>
                    <td class="kgr-table__cell">
                        <template v-if="isBeingEdited(trainerLicense)">
                            <div class="form-widget without-label">
                                <kgr-text-input
                                    v-model="editTrainerLicenses[getEditIndex(trainerLicense)].name"
                                />
                            </div>
                        </template>
                        <template v-else>
                            {{ trainerLicense.name }}
                        </template>
                    </td>
                    <td class="kgr-table__cell">
                        <template v-if="isBeingEdited(trainerLicense)">
                            <kgr-department-select
                                v-model="editTrainerLicenses[getEditIndex(trainerLicense)].department"
                                :club-id="clubId"
                                :hide-label="true"
                                exclude-if-basic-data-not-viewable
                            />
                        </template>
                        <template v-else>
                            {{ departmentName(trainerLicense) }}
                        </template>
                    </td>
                    <td
                        class="kgr-table__cell"
                    >
                        <template v-if="isBeingEdited(trainerLicense)">
                            <kgr-sports-select
                                v-model="editTrainerLicenses[getEditIndex(trainerLicense)].sportType"
                                :club-id="clubId"
                                :hide-label="true"
                                display="name"
                            />
                        </template>
                        <template v-else>
                            {{ sportTypeName(trainerLicense) }}
                        </template>
                    </td>
                    <td class="kgr-table__cell">
                        <template v-if="isBeingEdited(trainerLicense)">
                            <kgr-date-picker
                                v-model.trim="editTrainerLicenses[getEditIndex(trainerLicense)].delivered"
                                :value="editTrainerLicenses[getEditIndex(trainerLicense)].delivered"
                            />
                        </template>
                        <template v-else>
                            <kgr-date-picker
                                v-model.trim="trainerLicense.delivered"
                                :value="trainerLicense.delivered"
                                display-only
                            />
                        </template>
                    </td>
                    <td class="kgr-table__cell">
                        <template v-if="isBeingEdited(trainerLicense)">
                            <kgr-date-picker
                                v-model.trim="editTrainerLicenses[getEditIndex(trainerLicense)].validUntil"
                                :value="editTrainerLicenses[getEditIndex(trainerLicense)].validUntil"
                            />
                        </template>
                        <template v-else>
                            <kgr-date-picker
                                v-model.trim="trainerLicense.validUntil"
                                :value="trainerLicense.validUntil"
                                display-only
                            />
                        </template>
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--actions">
                        <kgr-icon-edit
                            :size="15"
                            :disabled="isBeingEdited(trainerLicense)"
                            class="kgr-table__action"
                            @click="edit(trainerLicense)"
                        />
                        <kgr-icon-delete
                            class="kgr-table__action"
                            @click="remove(trainerLicense)"
                        />
                    </td>
                </tr>
                <tr
                    class="kgr-table__row kgr-table__row--top"
                    :class="{ 'kgr-backgroundColor--error': newTrainerLicense.invalid}"
                >
                    <td class="kgr-table__cell">
                        <div class="form-widget without-label">
                            <kgr-trainer-license-level-select
                                :key="addTrainerLicense"
                                v-model="newTrainerLicense.level"
                                :allow-null-option="true"
                                @change="changedTrainerLicenseLevel"
                            />
                        </div>
                    </td>
                    <td class="kgr-table__cell">
                        <div class="form-widget without-label">
                            <kgr-text-input
                                v-model="newTrainerLicense.name"
                            />
                        </div>
                    </td>
                    <td class="kgr-table__cell">
                        <kgr-department-select
                            :key="addTrainerLicense"
                            v-model="newTrainerLicense.department"
                            :club-id="clubId"
                            :hide-label="true"
                            exclude-if-basic-data-not-viewable
                        />
                    </td>
                    <td
                        class="kgr-table__cell"
                    >
                        <kgr-sports-select
                            :key="addTrainerLicense"
                            v-model="newTrainerLicense.sportType"
                            :club-id="clubId"
                            :hide-label="true"
                            display="name"
                        />
                    </td>
                    <td class="kgr-table__cell">
                        <kgr-date-picker
                            v-model.trim="newTrainerLicense.delivered"
                            :value="newTrainerLicense.delivered"
                        />
                    </td>
                    <td class="kgr-table__cell">
                        <kgr-date-picker
                            v-model.trim="newTrainerLicense.validUntil"
                            :value="newTrainerLicense.validUntil"
                        />
                    </td>
                    <td class="kgr-table__cell">
                        &nbsp;
                    </td>
                </tr>
            </tbody>
        </table>
        <kgr-button
            class="button_save"
            :value="$t('saveChanges')"
            :pending="pendingCreate || pendingUpdate"
            @click="save()"
        />
    </div>
</template>

<script>
import ClubEmployeeTrainerLicenseRepository from '../../../js/repository/ClubEmployeeTrainerLicenseRepository';
import Date from '../../../js/date/Date';
import DatePicker from '../../common/form/picker/DateObjectPicker';
import DepartmentSelect from '../../common/form/select/DepartmentSelect.vue';
import Error from '../../../js/model/Error';
import IconDelete from '../../common/icon/IconDelete.vue';
import IconEdit from '../../common/icon/IconEdit.vue';
import KgrButton from '../../common/form/button/KgrButton.vue';
import SportsSelect from '../../common/form/select/SportsSelect.vue';
import TextInput from '../../common/form/input/TextInput.vue';
import TrainerLicense from '../../../js/dto/TrainerLicense';
import TrainerLicenseLevelSelect from '../../common/form/select/TrainerLicenseLevelSelect.vue';

export default {
    name: 'ClubEmployeeTrainerLicensesForm',
    components: {
        'kgr-department-select': DepartmentSelect,
        'kgr-sports-select': SportsSelect,
        'kgr-button': KgrButton,
        'kgr-icon-edit': IconEdit,
        'kgr-date-picker': DatePicker,
        'kgr-icon-delete': IconDelete,
        'kgr-text-input': TextInput,
        'kgr-trainer-license-level-select': TrainerLicenseLevelSelect,
    },
    props: {
        trainerLicenses: {
            type: Array,
            required: true,
        },
        clubId: {
            type: Number,
            required: true,
        },
        employeeId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            editTrainerLicenses: [],
            pendingCreate: false,
            pendingUpdate: false,
            newTrainerLicense: new TrainerLicense(this.employeeId),
            newTrainerLicenseIsInvalid: false,
            alertMessages: [],
            addTrainerLicense: 0,
            error: new Error(),
        }
    },
    methods: {
        isBeingEdited(trainerLicense) {
            return this.getEditIndex(trainerLicense) !== -1;
        },
        getEditIndex(trainerLicense) {
            return this.editTrainerLicenses.map(language => language.id).indexOf(trainerLicense.id);
        },
        departmentName(trainerLicense) {
            return trainerLicense.department != null ? trainerLicense.department.name : '';
        },
        sportTypeName(trainerLicense) {
            return trainerLicense.sportType != null ? trainerLicense.sportType.name : '';
        },
        remove(trainerLicense) {
            showConfirm(this.$t('confirm'), this.$t('Wollen sie wirklich die {level} {name} löschen?', {'level': this.$t('trainerLicenses.level.' + trainerLicense.level) ,'name': trainerLicense.name}), () => {
                this.editTrainerLicenses = this.editTrainerLicenses.filter(r => r.id !== trainerLicense.id);
                ClubEmployeeTrainerLicenseRepository
                    .removeTrainerLicense(this.employeeId, trainerLicense.id)
                    .then(() => {
                        this.$notify.success('{level} {name} erfolgreich gelöscht.', {'level': this.$t('trainerLicenses.level.' + trainerLicense.level) ,'name': trainerLicense.name});
                        this.emitUpdated();
                    })
                    .catch(() => {
                        this.$notify.error('saveChanges.error');
                    });
            }, 'Ok');
        },
        edit(trainerLicense) {
            if (this.isBeingEdited(trainerLicense)) {
                return;
            }

            this.editTrainerLicenses.push(trainerLicense);
        },
        save() {
            this.pendingCreate = true;
            this.pendingUpdate = true;

            let apiCalls = [];
            if (!this.newTrainerLicense.isEmptyName() && this.newTrainerLicense.delivered !== Date.today() && this.newTrainerLicense.isEmptyLevel()) {
                this.newTrainerLicense.invalid = true;
                this.$notify.error('Die Lizenzstufe der neuen Lizenz darf nicht leer sein!');
            } else if (this.newTrainerLicense.validUntil && this.newTrainerLicense.validUntil.isBefore(this.newTrainerLicense.delivered)) {
                this.newTrainerLicense.invalid = true;
                this.$notify.error('Das Gültig bis Datum darf nicht vor dem Ausgestellt/Vorgelegt am Datum sein!');
            } else {
                this.newTrainerLicense.invalid = false;
                apiCalls.push(this._createTrainerLicense());
            }
            if (this.editTrainerLicenses.length > 0) {
                apiCalls.push(this._updateTrainerLicenses(this.editTrainerLicenses));
            }
            if (apiCalls.length === 0) {
                this.pendingCreate = false;
                this.pendingUpdate = false;
                return;
            }

            ClubEmployeeTrainerLicenseRepository.all(apiCalls)
                .then(() => {
                    this.$notify.success('saveChanges.success');
                    this.emitUpdated();
                }).catch(() => {
                    this.$notify.error('Änderungen konnten nicht gespeichert werden. Bitte überprüfe die markierten Einträge.');
                })
                .finally(() => {
                    this.editTrainerLicenses = this.editTrainerLicenses.filter(trainerLicense => trainerLicense.invalid === true);
                    this.newTrainerLicense = new TrainerLicense(this.employeeId);
                    this.pendingCreate = false;
                    this.pendingUpdate = false;
                    this.alertMessages = [];
                });
        },
        _createTrainerLicense() {
            return ClubEmployeeTrainerLicenseRepository
                .createTrainerLicense(this.employeeId, this.newTrainerLicense)
                .then(() => {
                    this.newTrainerLicense = new TrainerLicense(this.employeeId);
                    this.newTrainerLicense.invalid = false;
                })
                .catch(() => {
                    this.newTrainerLicense.invalid = true;
                    this.alertMessages.push(this.$t('Änderungen konnten nicht gespeichert werden. Bitte überprüfe die markierten Einträge.'));
                })
        },
        _updateTrainerLicenses(editTrainerLicenses) {
            let correctLicenses = [];
            editTrainerLicenses.forEach(trainerLicense => {
                if (!trainerLicense.level) {
                    trainerLicense.invalid = true;
                    this.alertMessages.push(this.$t('Die Lizenzstufe der neuen Lizenz darf nicht leer sein!'));
                } else if (trainerLicense.validUntil && trainerLicense.validUntil.isBefore(trainerLicense.delivered)) {
                    trainerLicense.invalid = true;
                    this.alertMessages.push(this.$t('Das Gültig bis Datum darf nicht vor dem Ausgestellt/Vorgelegt am Datum sein!'));
                }
                else {
                    correctLicenses.push(trainerLicense);
                }
            });
            return ClubEmployeeTrainerLicenseRepository.updateAll(this.employeeId, correctLicenses)
                .catch(error => {
                    this.error = error;
                    this.$notify.error('Änderungen konnten nicht gespeichert werden. Bitte überprüfe die markierten Einträge.');
                })
        },
        emitUpdated() {
            this.addTrainerLicense++;
            this.$emit('updated');
        },
        getErrorMessageForLevel(trainerLicense) {
            if (!trainerLicense.level) {
                return this.$t('Die Lizenzstufe der neuen Lizenz darf nicht leer sein!');
            }
            return null;
        },
        changedTrainerLicenseLevel() {
            if (this.newTrainerLicense.level !== null) {
                this.newTrainerLicenseIsInvalid = false;
            }
        },
    }
}
</script>

<style scoped lang="scss" rel="stylesheet/scss">
.button_save {
    display: block;
    margin-left: auto;
}
</style>
